import { Form } from '@unform/web';
import styled from 'styled-components';

import { ButtonConfirm } from '../../../../components/ButtonConfirm';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;

  background: transparent;
  border: none;
  cursor: pointer;

  font-size: 13px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};

  &:hover {
    color: ${colors.onSurface};
  }
`;

export const ProfileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;

  padding: 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
`;

export const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: ${radius.DEFAULT};
  object-fit: cover;
`;

export const ProfileName = styled.h2`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.onSurface};
`;

export const ProfileMeta = styled.p`
  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;

export const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

interface StatusBadgeProps {
  active: boolean;
}

export const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 3px 10px;
  border-radius: ${radius.full};

  background-color: ${({ active }) =>
    active ? 'rgba(56, 193, 114, 0.15)' : 'rgba(255, 115, 81, 0.15)'};
  color: ${({ active }) => (active ? '#38c172' : colors.error)};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const PlatformBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 3px 10px;
  border-radius: ${radius.full};

  background-color: ${colors.tertiaryContainer};
  color: ${colors.onPrimary};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 3px 10px;
  border-radius: ${radius.full};

  background-color: ${colors.surfaceContainerHigh};
  color: ${colors.onSurfaceVariant};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const ActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface ActiveButtonProps {
  active: boolean;
}

export const ActiveToggleButton = styled.button<ActiveButtonProps>`
  padding: 10px 20px;
  border-radius: ${radius.DEFAULT};
  border: 1px solid
    ${({ active }) => (active ? colors.error : colors.outlineVariant)};
  cursor: pointer;

  background-color: transparent;
  color: ${({ active }) => (active ? colors.error : colors.onSurface)};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  white-space: nowrap;

  &:hover {
    background-color: ${({ active }) =>
      active ? 'rgba(255, 115, 81, 0.1)' : colors.surfaceContainer};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const Layout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SidebarTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

interface MembershipCardProps {
  selected: boolean;
}

export const MembershipCard = styled.button<MembershipCardProps>`
  text-align: left;
  padding: 14px 16px;

  border-radius: ${radius.md};
  border: 1px solid
    ${({ selected }) => (selected ? colors.primary : 'transparent')};
  background-color: ${({ selected }) =>
    selected ? colors.primaryContainer : colors.surfaceContainerLow};
  cursor: pointer;

  transition: border-color 150ms, background-color 150ms;

  &:hover {
    border-color: ${colors.primary};
  }
`;

export const MembershipCardName = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const MembershipCardSubtitle = styled.span`
  display: block;
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
  margin-top: 2px;
`;

export const MembershipCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;

  span {
    font-size: 11px;
    color: ${colors.onSurfaceVariant};
  }
`;

interface RoleBadgeProps {
  role: 'GESTOR' | 'FUNCIONÁRIO' | 'ALUNO' | 'PERSONALIZADO';
}

export const RoleBadge = styled.span<RoleBadgeProps>`
  padding: 2px 8px;
  border-radius: ${radius.full};

  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;

  background-color: ${({ role }) =>
    role === 'GESTOR' ? colors.primary : colors.surfaceContainerHigh};
  color: ${({ role }) =>
    role === 'GESTOR' ? colors.onPrimary : colors.onSurfaceVariant};
`;

export const AddMembershipCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 14px 16px;
  border-radius: ${radius.md};
  border: 1px dashed ${colors.outlineVariant};
  cursor: pointer;

  background-color: transparent;
  color: ${colors.primary};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &:hover {
    background-color: ${colors.surfaceContainerLow};
  }
`;

export const DetailPanel = styled.div`
  padding: 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
  min-height: 200px;
`;

export const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`;

export const DetailEyebrow = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const DetailGymName = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.onSurface};
  margin: 2px 0 4px;
`;

export const DetailMeta = styled.span`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;

export const DetailEmptyState = styled.p`
  font-size: 13px;
  color: ${colors.onSurfaceVariant};
  text-align: center;
  padding: 40px 0;
`;

export const RemoveButton = styled.button`
  padding: 6px 14px;
  border-radius: ${radius.full};
  border: 1px solid ${colors.error};
  cursor: pointer;

  background-color: transparent;
  color: ${colors.error};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  white-space: nowrap;

  &:hover {
    background-color: rgba(255, 115, 81, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const MembershipForm = styled(Form)`
  display: flex;
  flex-direction: column;
`;

export const SaveButton = styled(ButtonConfirm)`
  width: auto;
  align-self: flex-end;
  height: 40px;
  padding: 0 20px;
  border-radius: ${radius.DEFAULT};
  margin-top: 16px;
`;
